import { useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, CreditCard, Calendar, ChartLine, Users } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const DemoPage = () => {
  const navigate = useNavigate();

  const features = [
    { icon: Calendar, title: 'Online Booking System', desc: '24/7 appointment scheduling for your clients' },
    { icon: CreditCard, title: 'Secure Payments', desc: 'Accept payments instantly with Stripe integration' },
    { icon: Users, title: 'Admin Dashboard', desc: 'Manage all appointments in one place' },
    { icon: ChartLine, title: 'Business Insights', desc: 'Track bookings and revenue at a glance' }
  ];

  const services = [
    'Eyebrow Threading - $15',
    'Upper Lip Threading - $8', 
    'Full Face Threading - $35',
    'Brazilian Wax - $60',
    'Bikini Wax - $40',
    'Full Leg Wax - $70',
    '+ 6 more services'
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="py-20 px-6 text-center border-b border-border">
        <div className="max-w-4xl mx-auto">
          <div className="inline-block px-4 py-2 bg-accent-soft rounded-full text-sm font-medium text-accent mb-6">
            ✨ Beauty Salon Demo Template
          </div>
          <h1 className="text-4xl sm:text-5xl font-serif font-medium mb-6 tracking-tight">
            Your Complete Booking & Payment Solution
          </h1>
          <p className="text-lg text-foreground-secondary mb-8 max-w-2xl mx-auto">
            A professional website template designed for beauty salons, threading studios, and waxing businesses. 
            Accept bookings online, process payments securely, and manage your business efficiently.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              data-testid="view-live-demo-btn"
              size="lg"
              onClick={() => navigate('/demos/beauty-salon')}
              className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2"
            >
              View Live Demo
              <ArrowRight size={20} weight="bold" />
            </Button>
            <Button
              data-testid="view-admin-demo-btn"
              size="lg"
              variant="outline"
              onClick={() => navigate('/demos/beauty-salon/admin')}
              className="gap-2"
            >
              Try Admin Dashboard
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6 bg-background-subtle">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-serif font-medium text-center mb-12">What's Included</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <div key={idx} className="bg-background-paper rounded-md border border-border p-6">
                <div className="w-12 h-12 rounded-full bg-accent-soft flex items-center justify-center mb-4">
                  <feature.icon size={24} weight="light" className="text-accent" />
                </div>
                <h3 className="font-medium mb-2">{feature.title}</h3>
                <p className="text-sm text-foreground-secondary">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-serif font-medium mb-6">12 Professional Services</h2>
              <p className="text-foreground-secondary mb-6">
                All your threading and waxing services pre-configured with pricing, 
                descriptions, and duration. Easy to modify or add more.
              </p>
              <ul className="space-y-3">
                {services.map((service, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <CheckCircle size={20} weight="fill" className="text-green-600 flex-shrink-0" />
                    <span>{service}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-accent-soft rounded-md p-8 border border-accent">
              <h3 className="font-medium mb-4">How It Works</h3>
              <ol className="space-y-4">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent text-white text-sm flex items-center justify-center">1</span>
                  <div>
                    <div className="font-medium">Customer Books Online</div>
                    <div className="text-sm text-foreground-secondary">Choose service, date & time</div>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent text-white text-sm flex items-center justify-center">2</span>
                  <div>
                    <div className="font-medium">Secure Payment</div>
                    <div className="text-sm text-foreground-secondary">Pay instantly with Stripe</div>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent text-white text-sm flex items-center justify-center">3</span>
                  <div>
                    <div className="font-medium">You Approve & Serve</div>
                    <div className="text-sm text-foreground-secondary">Manage from admin dashboard</div>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-20 px-6 bg-background-subtle border-t border-border">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-serif font-medium mb-6">Built with Modern Technology</h2>
          <p className="text-foreground-secondary mb-8">
            Enterprise-grade tech stack ensuring security, speed, and reliability
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {['React Framework', 'Secure Database', 'Stripe Payments', 'Mobile Responsive'].map((tech, idx) => (
              <div key={idx} className="bg-background-paper rounded-md border border-border p-4">
                <CheckCircle size={24} weight="fill" className="text-green-600 mx-auto mb-2" />
                <div className="text-sm font-medium">{tech}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-serif font-medium mb-6">Ready to See It in Action?</h2>
          <p className="text-foreground-secondary mb-8">
            Explore the customer booking flow and admin dashboard. 
            Test all features with demo data.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/demos/beauty-salon')}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              View Customer Experience
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/demos/beauty-salon/admin')}
            >
              Access Admin Dashboard
            </Button>
          </div>
          <div className="mt-8 p-6 bg-accent-soft rounded-md border border-accent inline-block">
            <p className="text-sm font-medium mb-2">Admin Login Credentials:</p>
            <p className="text-sm text-foreground-secondary">Username: <code className="font-mono bg-background px-2 py-1 rounded">admin</code></p>
            <p className="text-sm text-foreground-secondary">Password: <code className="font-mono bg-background px-2 py-1 rounded">admin123</code></p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DemoPage;